import {
  afterRenderEffect,
  DestroyRef,
  inject,
  Service,
  signal,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

const iconFonts = [
  { key: 'symbols', family: 'Material Symbols Outlined', readyClass: 'syrf-symbols-ready' },
  { key: 'icons', family: 'Material Icons', readyClass: 'syrf-icons-ready' },
  { key: 'outlined', family: 'Material Icons Outlined', readyClass: 'syrf-icons-outlined-ready' },
] as const;

type IconFont = (typeof iconFonts)[number];
type FontStatus = 'loading' | 'ready' | 'failed';

/** Keeps ligature names hidden until their own font has actually loaded. */
@Service()
export class IconFontReadinessService {
  private readonly _document = inject(DOCUMENT);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _status = signal<Readonly<Record<IconFont['key'], FontStatus>>>({
    symbols: 'loading',
    icons: 'loading',
    outlined: 'loading',
  });
  private _started = false;

  readonly status = this._status.asReadonly();

  constructor() {
    // The head CSS protects the first paint, before Angular starts. Updating
    // root classes here also covers CDK overlays outside the application root.
    afterRenderEffect({
      write: () => {
        const status = this._status();
        for (const { key, readyClass } of iconFonts) {
          this._document.documentElement.classList.toggle(
            readyClass,
            status[key] === 'ready'
          );
        }
      },
    });
  }

  /** Deliberately returns void: unavailable fonts must not block bootstrap. */
  start(): void {
    if (this._started) return;
    this._started = true;
    for (const font of iconFonts) {
      void this._load(font);
    }
  }

  private async _load({ key, family }: IconFont): Promise<void> {
    let status: FontStatus = 'failed';
    try {
      const faces = await this._document.fonts.load(`400 24px "${family}"`, 'thumb_up');
      // An empty result (no matching @font-face) is not readiness. Neither
      // document.fonts.ready nor check() establishes that this font loaded.
      if (faces.length > 0 && faces.every((face) => face.status === 'loaded')) {
        status = 'ready';
      }
    } catch {
      // Offline, blocked or unsupported font loading leaves the CSS guard on.
      // No timeout may reveal fallback text. A reload can retry failed fonts.
    }
    if (!this._destroyRef.destroyed) {
      this._status.update((current) => ({ ...current, [key]: status }));
    }
  }
}
